import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sparkles, Trash2, Upload, Power } from "lucide-react";

const OCCASIONS = ["जन्मदिन", "वर्षगाँठ", "बधाई", "पदोन्नति", "शादी की सालगिरह", "पुण्य तिथि", "अन्य"];

export default function WishesAdminPage() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ occasion: "जन्मदिन", recipient_name: "", from_name: "", message: "", photo_url: "", valid_till: "", active: true });
  const [uploading, setUploading] = useState(false);
  const photoRef = useRef();

  const load = () => api.get("/admin/wishes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload?watermark=false", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, photo_url: data.url }));
      toast.success("फ़ोटो अपलोड हो गयी");
    } catch (e) { toast.error(formatError(e)); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    if (!form.recipient_name.trim()) { toast.error("जिसे शुभकामना देनी है, उसका नाम दें"); return; }
    try {
      await api.post("/admin/wishes", form);
      toast.success("शुभकामना जोड़ी गयी");
      setForm({ occasion: "जन्मदिन", recipient_name: "", from_name: "", message: "", photo_url: "", valid_till: "", active: true });
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const toggle = async (id, active) => { await api.patch(`/admin/wishes/${id}`, { active }); load(); };
  const del = async (id) => { if (!window.confirm("हटाएँ?")) return; await api.delete(`/admin/wishes/${id}`); load(); };

  return (
    <div data-testid="admin-wishes-page">
      <h1 className="font-devnagri-heading text-3xl font-black text-[#0F172A] flex items-center gap-3 mb-2"><Sparkles className="w-6 h-6 text-amber-500"/>शुभकामनाएँ · Wishes</h1>
      <p className="text-sm text-slate-500 mb-6">जन्मदिन/बधाई/शुभकामना जोड़ें — अगले e-Paper में प्रिंट होगी</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <h3 className="font-bold text-[#0F172A] mb-3">नयी शुभकामना जोड़ें</h3>
          <div className="space-y-3">
            <select value={form.occasion} onChange={(e) => setForm({ ...form, occasion: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-slate-200" data-testid="wish-occasion">
              {OCCASIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <Input placeholder="जिसे शुभकामना देनी है (नाम) *" value={form.recipient_name}
              onChange={(e) => setForm({ ...form, recipient_name: e.target.value })} data-testid="wish-to"/>
            <Input placeholder="किसकी ओर से (नाम) — वैकल्पिक" value={form.from_name}
              onChange={(e) => setForm({ ...form, from_name: e.target.value })} data-testid="wish-from"/>
            <Textarea rows={3} maxLength={300} placeholder="संदेश (वैकल्पिक)" value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })} data-testid="wish-msg"/>
            <div className="flex items-center gap-3">
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 rounded-md border border-dashed border-slate-300 hover:border-[#EA580C] text-sm">
                <Upload className="w-4 h-4"/>{uploading ? "अपलोड…" : "फ़ोटो"}
                <input ref={photoRef} type="file" accept="image/*" className="hidden"
                  onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])} data-testid="wish-photo-input"/>
              </label>
              {form.photo_url && <img src={form.photo_url} alt="" className="w-14 h-14 rounded-full object-cover"/>}
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-widest text-slate-500 font-bold block mb-1">वैध तक (वैकल्पिक)</label>
              <Input type="date" value={form.valid_till} onChange={(e) => setForm({ ...form, valid_till: e.target.value })}/>
            </div>
            <Button onClick={submit} className="w-full bg-amber-600 hover:bg-amber-700 text-white" data-testid="wish-submit"><Sparkles className="w-4 h-4 mr-2"/>जोड़ें</Button>
          </div>
        </div>

        <div>
          <h3 className="font-bold text-[#0F172A] mb-3">सक्रिय शुभकामनाएँ ({items.filter(x => x.active).length})</h3>
          <div className="space-y-2 max-h-[600px] overflow-y-auto">
            {items.map((w) => (
              <div key={w.id} className={`bg-white border rounded-lg p-3 ${w.active ? "border-amber-300" : "border-slate-200 opacity-50"}`} data-testid={`wish-${w.id}`}>
                <div className="flex items-start gap-3">
                  {w.photo_url && <img src={w.photo_url} alt="" className="w-12 h-12 rounded-full object-cover shrink-0"/>}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[10px] uppercase tracking-widest bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded font-bold">{w.occasion}</span>
                      <span className="font-bold text-[#0F172A]">{w.recipient_name}</span>
                    </div>
                    {w.message && <div className="text-xs text-slate-600 mt-1 line-clamp-2">{w.message}</div>}
                    {w.from_name && <div className="text-[10px] text-amber-700 mt-1">— {w.from_name}</div>}
                  </div>
                  <div className="flex flex-col gap-1">
                    <button onClick={() => toggle(w.id, !w.active)} className={`p-1 rounded ${w.active ? "text-emerald-600" : "text-slate-400"}`} data-testid={`wish-toggle-${w.id}`}><Power className="w-3.5 h-3.5"/></button>
                    <button onClick={() => del(w.id)} className="p-1 text-red-500 hover:bg-red-50 rounded" data-testid={`wish-del-${w.id}`}><Trash2 className="w-3.5 h-3.5"/></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
